import { useRef, useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/i18n/LanguageContext";

interface AnimatedNumberProps {
  target: number;
  suffix: string;
  active: boolean;
}

const AnimatedNumber = ({ target, suffix, active }: AnimatedNumberProps) => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!active) return;
    let start = 0;
    const duration = 1800;
    const startTime = performance.now();

    const step = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      start = Math.round(eased * target);
      setCurrent(start);
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [active, target]);

  return (
    <span>
      {current.toLocaleString()}
      {suffix}
    </span>
  );
};

const AboutSection = () => {
  const statsRef = useRef<HTMLDivElement>(null);
  const [statsVisible, setStatsVisible] = useState(false);
  const { t, lang } = useLanguage();

  const fontFamily = lang === "ar" ? "'Noto Sans Arabic', 'Nunito', sans-serif" : "'Nunito', sans-serif";

  const stats = [
    { target: 70, suffix: "+", label: t("about.stat.products") },
    { target: 10, suffix: "+", label: t("about.stat.brands") },
    { target: 7, suffix: "+", label: t("about.stat.years") },
    { target: 5, suffix: "K+", label: t("about.stat.customers") },
  ];

  const handleIntersect = useCallback((entries: IntersectionObserverEntry[]) => {
    if (entries[0].isIntersecting) setStatsVisible(true);
  }, []);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(handleIntersect, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [handleIntersect]);

  return (
    <section
      id="about"
      className="relative overflow-hidden bg-white px-6 py-24 sm:px-10 lg:px-16"
    >
      <div
        className="pointer-events-none absolute -top-32 -right-32 h-96 w-96 rounded-full opacity-20 blur-3xl"
        style={{ background: "radial-gradient(circle, #E88CA4, transparent 70%)" }}
      />
      <div
        className="pointer-events-none absolute -bottom-32 -left-32 h-80 w-80 rounded-full opacity-15 blur-3xl"
        style={{ background: "radial-gradient(circle, #f8c8d8, transparent 70%)" }}
      />

      <div className="relative mx-auto max-w-5xl">
        <p
          className="mb-3 text-xs font-semibold uppercase tracking-[0.25em]"
          style={{ fontFamily, color: "#E88CA4" }}
        >
          {t("about.label")}
        </p>

        <h2
          className="text-4xl font-bold sm:text-5xl overflow-visible"
          style={{
            fontFamily: "'Poppins', sans-serif",
            background: "linear-gradient(135deg, #E88CA4 0%, #a8405c 50%, #f8c8d8 100%)",
            backgroundSize: "200% 100%",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            color: "transparent",
            WebkitTextFillColor: "transparent",
            animation: "gradientShift 5s ease infinite",
            lineHeight: 1.4,
            paddingTop: "0.15em",
          }}
        >
          {t("about.title")}
        </h2>

        <div className="mt-6 w-72">
          <svg viewBox="0 0 260 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
            <defs>
              <linearGradient id="curvyGrad" x1="0" y1="10" x2="260" y2="10" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#ffffff" />
                <stop offset="25%" stopColor="#f8c8d8" />
                <stop offset="55%" stopColor="#E88CA4" />
                <stop offset="100%" stopColor="#a8405c" />
              </linearGradient>
            </defs>
            <path
              d="M2 12 Q 30 2, 55 10 T 110 10 T 165 10 T 220 10 T 258 8"
              stroke="url(#curvyGrad)"
              strokeWidth="2.5"
              strokeLinecap="round"
              fill="none"
            />
          </svg>
        </div>

        <div className="mt-10 grid gap-10 md:grid-cols-2">
          <div>
            <p
              className="text-base leading-relaxed text-gray-600"
              style={{ fontFamily }}
            >
              {t("about.p1")}
            </p>
            <p
              className="mt-5 text-base leading-relaxed text-gray-600"
              style={{ fontFamily }}
            >
              {t("about.p2")}
            </p>
          </div>

          <div ref={statsRef}>
            <div className="grid grid-cols-2 gap-6">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="group rounded-2xl border border-gray-100 bg-gray-50/50 p-5 text-center transition-all duration-300 hover:border-[#E88CA4]/30 hover:shadow-lg hover:shadow-[#E88CA4]/10"
                >
                  <p
                    className="text-3xl font-bold"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      color: "#C9607E",
                    }}
                  >
                    <AnimatedNumber target={stat.target} suffix={stat.suffix} active={statsVisible} />
                  </p>
                  <p
                    className="mt-1 text-xs font-semibold uppercase tracking-wider text-gray-400"
                    style={{ fontFamily }}
                  >
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
